import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Link as RouterLink } from 'react-router-dom';
import { getEnvironmentImportReports } from './environmentImportApi.js';

const statusConfig = {
  running: { label: 'En cours', color: 'info' },
  success: { label: 'Réussi', color: 'success' },
  partial_success: { label: 'Partiellement réussi', color: 'warning' },
  failed: { label: 'Échec', color: 'error' },
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString('fr-FR');
};

const formatDuration = (value) => {
  if (!Number.isFinite(value)) return '—';
  if (value < 1000) return `${value} ms`;
  if (value < 60000) return `${(value / 1000).toFixed(1)} s`;
  const minutes = Math.floor(value / 60000);
  const seconds = Math.round((value % 60000) / 1000);
  return `${minutes} min ${seconds} s`;
};

const actorLabel = (actor) =>
  actor?.email || actor?.username || actor?.name || actor?.id || 'Admin non identifié';

const EnvironmentImportReports = () => {
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
  const [filters, setFilters] = useState({ status: '', sourceProvider: '', targetProvider: '', discipline: '' });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await getEnvironmentImportReports({
          page: pagination.page,
          limit: pagination.limit,
          ...Object.fromEntries(
            Object.entries(appliedFilters).filter(([, value]) => String(value || '').trim())
          ),
        });
        if (!mounted) return;
        setItems(response?.data?.items || []);
        setPagination((current) => ({
          ...current,
          ...(response?.data?.pagination || {}),
        }));
      } catch (err) {
        if (!mounted) return;
        setError(
          err?.response?.data?.message || err?.message || "Impossible de charger l'historique des imports."
        );
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => { mounted = false; };
  }, [pagination.page, pagination.limit, appliedFilters, refreshKey]);

  const applyFilters = () => {
    setPagination((current) => ({ ...current, page: 1 }));
    setAppliedFilters({ ...filters });
  };

  const resetFilters = () => {
    const empty = { status: '', sourceProvider: '', targetProvider: '', discipline: '' };
    setFilters(empty);
    setPagination((current) => ({ ...current, page: 1 }));
    setAppliedFilters(empty);
  };

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', py: 3 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Rapports d'import environnement
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Historique des imports réellement exécutés, avec statut, utilisateur, durée et détail technique des écritures.
          </Typography>
        </Box>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(170px, 1fr))' },
                gap: 1.5,
              }}
            >
              <TextField
                select
                label="Statut"
                value={filters.status}
                onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))}
              >
                <MenuItem value="">Tous</MenuItem>
                {Object.entries(statusConfig).map(([value, config]) => (
                  <MenuItem key={value} value={value}>{config.label}</MenuItem>
                ))}
              </TextField>
              <TextField
                label="Provider source"
                value={filters.sourceProvider}
                onChange={(event) => setFilters((current) => ({ ...current, sourceProvider: event.target.value }))}
              />
              <TextField
                label="Provider cible"
                value={filters.targetProvider}
                onChange={(event) => setFilters((current) => ({ ...current, targetProvider: event.target.value }))}
              />
              <TextField
                label="Discipline"
                value={filters.discipline}
                onChange={(event) => setFilters((current) => ({ ...current, discipline: event.target.value }))}
              />
            </Box>

            <Stack direction="row" spacing={1} justifyContent="flex-end" useFlexGap flexWrap="wrap">
              <Button variant="text" onClick={resetFilters}>Réinitialiser</Button>
              <Button
                variant="outlined"
                startIcon={<RefreshOutlinedIcon />}
                onClick={() => setRefreshKey((value) => value + 1)}
              >
                Actualiser
              </Button>
              <Button variant="contained" startIcon={<SearchOutlinedIcon />} onClick={applyFilters}>
                Filtrer
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {error && <Alert severity="error">{error}</Alert>}

        <Paper variant="outlined">
          {loading ? (
            <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : items.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <Typography color="text.secondary">Aucun rapport ne correspond aux critères.</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Début</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell>Utilisateur</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Cible(s)</TableCell>
                    <TableCell>Compétition(s)</TableCell>
                    <TableCell>Résultat</TableCell>
                    <TableCell>Durée</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((report) => {
                    const status = statusConfig[report?.status] || statusConfig.failed;
                    const summary = report?.executionSummary || {};
                    return (
                      <TableRow key={report._id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(report.startedAt)}</TableCell>
                        <TableCell>
                          <Chip size="small" label={status.label} color={status.color} />
                        </TableCell>
                        <TableCell>{actorLabel(report.actor)}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{report?.source?.provider || '—'}</Typography>
                          <Typography variant="caption" color="text.secondary">{report?.source?.discipline || '—'}</Typography>
                        </TableCell>
                        <TableCell>{(report?.targetProviders || []).join(', ') || '—'}</TableCell>
                        <TableCell>
                          {(report?.targets || []).map((target) => target.sourceCompetitionName).filter(Boolean).join(', ') || '—'}
                        </TableCell>
                        <TableCell sx={{ minWidth: 190 }}>
                          <Typography variant="caption" display="block">
                            {summary.created || 0} créé(s) · {summary.updated || 0} modifié(s)
                          </Typography>
                          <Typography variant="caption" display="block" color={summary.failed ? 'error.main' : 'text.secondary'}>
                            {summary.failed || 0} échec(s)
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDuration(report.durationMs)}</TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            component={RouterLink}
                            to={`/admin/environment-import/reports/${report._id}`}
                            startIcon={<VisibilityOutlinedIcon />}
                          >
                            Détail
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {pagination.pages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Pagination
              page={pagination.page}
              count={pagination.pages}
              onChange={(_, page) => setPagination((current) => ({ ...current, page }))}
            />
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default EnvironmentImportReports;
